import React, { useState } from 'react';
import API from '../api';
import Navbar from '../components/Navbar';

const Reports = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const getPeriodDates = (p) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    switch (p) {
      case 'today':
        return { start: todayStr, end: todayStr };
      case '7days': {
        const start = new Date(today);
        start.setDate(today.getDate() - 6);
        return { start: start.toISOString().split('T')[0], end: todayStr };
      }
      case 'month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: start.toISOString().split('T')[0], end: todayStr };
      }
      case 'lastmonth': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
      }
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: todayStr, end: todayStr };
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { start, end } = getPeriodDates(period);
      const res = await API.get(`/invoices/profit-report?start_date=${start}&end_date=${end}`);
      setReport({ ...res.data, period_start: start, period_end: end });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handlePrint = () => window.print();

  const periodLabels = {
    today: 'Today',
    '7days': 'Last 7 Days',
    month: 'This Month',
    lastmonth: 'Last Month',
    custom: 'Custom Range',
  };

  return (
    <div>
      <Navbar />
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @media print {
          body * { visibility: hidden; }
          #report, #report * { visibility: visible; }
          #report { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none; }
          @page { margin: 0; size: auto; }
        }
      `}</style>
      <div style={styles.container} className="container">
        <div style={styles.header}>
          <h2 style={styles.title}>P&L Report</h2>
          {report && (
            <button onClick={handlePrint} style={styles.printBtn} className="no-print">
              🖨️ Print Report
            </button>
          )}
        </div>

        <div style={styles.filterCard} className="no-print">
          <div style={styles.periodBtns}>
            {['today', '7days', 'month', 'lastmonth', 'custom'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={period === p ? styles.periodActive : styles.periodBtn}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div style={styles.dateRow}>
              <input style={styles.dateInput} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <span style={{ color: '#888' }}>to</span>
              <input style={styles.dateInput} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          )}
          <button onClick={fetchReport} style={styles.button} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>

        {loading && (
          <div style={styles.loaderWrapper}>
            <div style={styles.spinner}></div>
            <p style={styles.loaderText}>Generating report...</p>
          </div>
        )}

        {report && !loading && (
          <div id="report">
            <div style={styles.reportHeader}>
              <h3 style={styles.reportTitle}>🧾 {JSON.parse(localStorage.getItem('user'))?.shop_name}</h3>
              <p style={styles.reportDate}>
                P&L Report — {periodLabels[period]} ({report.period_start} to {report.period_end})
              </p>
            </div>

            <div style={styles.cards} className="cards">
              <div style={styles.card}>
                <p style={styles.label}>Total Invoices</p>
                <p style={styles.value}>{report.summary.total_invoices}</p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Total Revenue</p>
                <p style={styles.value}>LKR {parseFloat(report.summary.total_revenue).toFixed(2)}</p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Cost of Goods</p>
                <p style={{ ...styles.value, color: '#ef4444' }}>LKR {parseFloat(report.summary.total_cost_of_goods).toFixed(2)}</p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Gross Profit</p>
                <p style={{ ...styles.value, color: '#16a34a' }}>LKR {parseFloat(report.summary.gross_profit).toFixed(2)}</p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Total Expenses</p>
                <p style={{ ...styles.value, color: '#ef4444' }}>LKR {parseFloat(report.summary.total_expenses).toFixed(2)}</p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Net Profit</p>
                <p style={{ ...styles.value, color: report.summary.net_profit >= 0 ? '#16a34a' : '#ef4444' }}>
                  LKR {parseFloat(report.summary.net_profit).toFixed(2)}
                </p>
              </div>
              <div style={styles.card}>
                <p style={styles.label}>Profit Margin</p>
                <p style={{ ...styles.value, color: report.summary.profit_margin >= 0 ? '#16a34a' : '#ef4444' }}>
                  {report.summary.profit_margin}%
                </p>
              </div>
            </div>

            <div style={styles.profitSummaryBox}>
              <h3 style={styles.sectionTitle}>💰 Profit & Loss Summary</h3>
              
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '0.75rem', fontWeight: '500' }}>REVENUE</p>
              <div style={styles.profitRow}>
                <span>💵 Cash Sales</span>
                <span style={{ color: '#16a34a' }}>LKR {parseFloat(report.summary.cash_revenue || 0).toFixed(2)}</span>
              </div>
              <div style={styles.profitRow}>
                <span>💳 Credit Sales</span>
                <span style={{ color: '#f59e0b' }}>LKR {parseFloat(report.summary.credit_revenue || 0).toFixed(2)}</span>
              </div>
              <div style={styles.profitRow}>
                <span style={{ fontWeight: '500' }}>Total Revenue</span>
                <span style={{ fontWeight: '500' }}>LKR {parseFloat(report.summary.total_revenue).toFixed(2)}</span>
              </div>

              <div style={styles.profitDivider}></div>
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '0.75rem', fontWeight: '500' }}>COST & PROFIT</p>
              <div style={styles.profitRow}>
                <span style={{ color: '#ef4444' }}>Cost of Goods Sold</span>
                <span style={{ color: '#ef4444' }}>- LKR {parseFloat(report.summary.total_cost_of_goods).toFixed(2)}</span>
              </div>
              <div style={styles.profitRow}>
                <span style={{ fontWeight: '500' }}>Gross Profit</span>
                <span style={{ color: '#16a34a', fontWeight: '500' }}>LKR {parseFloat(report.summary.gross_profit).toFixed(2)}</span>
              </div>
              <div style={styles.profitRow}>
                <span style={{ color: '#ef4444' }}>Total Expenses</span>
                <span style={{ color: '#ef4444' }}>- LKR {parseFloat(report.summary.total_expenses).toFixed(2)}</span>
              </div>
              <div style={styles.profitDivider}></div>
              <div style={{ ...styles.profitRow, fontSize: '18px', fontWeight: 'bold' }}>
                <span>Net Profit</span>
                <span style={{ color: report.summary.net_profit >= 0 ? '#16a34a' : '#ef4444' }}>
                  LKR {parseFloat(report.summary.net_profit).toFixed(2)}
                </span>
              </div>

              <div style={styles.profitDivider}></div>
              <p style={{ fontSize: '13px', color: '#888', marginBottom: '0.75rem', fontWeight: '500' }}>💰 CASHIER SUMMARY</p>
             <div style={styles.profitRow}>
                <span>Cash Sales</span>
                <span style={{ color: '#16a34a' }}>LKR {parseFloat(report.summary.cash_revenue || 0).toFixed(2)}</span>
              </div>
              {report.summary.customer_payments_received > 0 && (
                <div style={styles.profitRow}>
                  <span style={{ color: '#16a34a' }}>💰 Customer payments received</span>
                  <span style={{ color: '#16a34a' }}>+ LKR {parseFloat(report.summary.customer_payments_received).toFixed(2)}</span>
                </div>
              )}
              <div style={styles.profitRow}>
                <span style={{ color: '#ef4444' }}>Less: Expenses</span>
                <span style={{ color: '#ef4444' }}>- LKR {parseFloat(report.summary.total_expenses).toFixed(2)}</span>
              </div>
              <div style={styles.profitDivider}></div>
              <div style={{ ...styles.profitRow, fontSize: '16px', fontWeight: 'bold' }}>
                <span>💵 Net Cash in Cashier</span>
                <span style={{ color: report.summary.net_cash_in_cashier >= 0 ? '#16a34a' : '#ef4444' }}>
                  LKR {parseFloat(report.summary.net_cash_in_cashier || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ ...styles.profitRow, marginTop: '0.5rem' }}>
                <span style={{ color: '#f59e0b' }}>💳 Credit (not in cashier)</span>
                <span style={{ color: '#f59e0b' }}>LKR {parseFloat(report.summary.credit_revenue || 0).toFixed(2)}</span>
              </div>
              {report.summary.old_invoice_returns > 0 && (
                <div style={{ ...styles.profitRow, marginTop: '0.25rem' }}>
                  <span style={{ color: '#ef4444', fontSize: '12px' }}>↩️ Refunds paid today for old invoices</span>
                  <span style={{ color: '#ef4444', fontSize: '12px' }}>- LKR {parseFloat(report.summary.old_invoice_returns).toFixed(2)}</span>
                </div>
              )}
            </div>

            {report.return_summary?.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>↩️ Return Summary</h3>
                <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Invoice #</th>
                      <th style={styles.th}>Invoice Date</th>
                      <th style={styles.th}>Returned On</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.return_summary.map((r, i) => (
                      <tr key={i}>
<td style={styles.td}>
                          #{r.invoice_number}
                          {r.archived && <span style={{ fontSize: '10px', color: '#888', marginLeft: '4px' }}>(deleted)</span>}
                        </td>                        <td style={styles.td}>{new Date(r.invoice_date).toLocaleDateString()}</td>
                        <td style={styles.td}>{new Date(r.returned_at).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          {r.same_day
                            ? <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: 'bold' }}>Same-day</span>
                            : <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: 'bold' }}>Old invoice</span>
                          }
                        </td>
                        <td style={{ ...styles.td, color: '#ef4444', fontWeight: 'bold' }}>LKR {parseFloat(r.return_amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </>
            )}

            {report.top_products?.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>🏆 Top Products by Profit</h3>
                <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Product</th>
                      <th style={styles.th}>Qty Sold</th>
                      <th style={styles.th}>Revenue (LKR)</th>
                      <th style={styles.th}>Profit (LKR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.top_products.map((p, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{p.name}</td>
                        <td style={styles.td}>{p.total_sold}</td>
                        <td style={styles.td}>{parseFloat(p.total_revenue).toFixed(2)}</td>
                        <td style={{ ...styles.td, color: '#16a34a', fontWeight: 'bold' }}>
                          {parseFloat(p.total_profit).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </>
            )}

            {report.daily_profit?.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>📅 Daily Breakdown</h3>
                <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Revenue (LKR)</th>
                      <th style={styles.th}>Cost (LKR)</th>
                      <th style={styles.th}>Expenses (LKR)</th>
                      <th style={styles.th}>Net Profit (LKR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.daily_profit.map((d, i) => {
                      const profit = parseFloat(d.revenue) - parseFloat(d.cost) - parseFloat(d.expenses);
                      return (
                        <tr key={i}>
                          <td style={styles.td}>{new Date(d.date).toLocaleDateString()}</td>
                          <td style={styles.td}>{parseFloat(d.revenue).toFixed(2)}</td>
                          <td style={{ ...styles.td, color: '#ef4444' }}>{parseFloat(d.cost).toFixed(2)}</td>
                          <td style={{ ...styles.td, color: '#ef4444' }}>{parseFloat(d.expenses).toFixed(2)}</td>
                          <td style={{ ...styles.td, color: profit >= 0 ? '#16a34a' : '#ef4444', fontWeight: 'bold' }}>
                            {profit.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              </>
            )}

            {report.expenses?.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>💸 Expenses Breakdown</h3>
                <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Title</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Amount (LKR)</th>
                      <th style={styles.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.expenses.map((exp, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{exp.title}</td>
                        <td style={styles.td}>{exp.category}</td>
                        <td style={{ ...styles.td, color: '#ef4444' }}>{parseFloat(exp.amount).toFixed(2)}</td>
                        <td style={styles.td}>{new Date(exp.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '24px', margin: 0 },
  printBtn: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  filterCard: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' },
  periodBtns: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' },
  periodBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  periodActive: { padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  dateRow: { display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' },
  dateInput: { padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
  button: { padding: '10px 24px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  loaderWrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e0e7ff', borderTop: '4px solid #4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loaderText: { marginTop: '1rem', color: '#888', fontSize: '14px' },
  reportHeader: { textAlign: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #4f46e5' },
  reportTitle: { fontSize: '24px', margin: '0 0 0.5rem' },
  reportDate: { color: '#888', fontSize: '14px' },
  cards: { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' },
  card: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flex: 1, minWidth: '140px' },
  label: { color: '#888', fontSize: '13px', margin: '0 0 0.5rem' },
  value: { fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#4f46e5' },
  profitSummaryBox: { background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '500px', marginBottom: '2rem' },
  sectionTitle: { fontSize: '18px', margin: '1.5rem 0 1rem' },
  profitRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '6px 0' },
  profitDivider: { borderTop: '1px solid #ddd', margin: '8px 0' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' },
  th: { padding: '12px 16px', background: '#4f46e5', color: '#fff', textAlign: 'left', fontSize: '14px' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
};

export default Reports;